import React from "react";
import { Link } from "react-router-dom";
import TableCell from "@material-ui/core/TableCell";
import moment from "moment";
import _ from "lodash";
import numberFormatter from "utils/numberFormatter";
import FundTableOptionsMenu from "components/Global/OptionsMenu/FundTable/FundTableOptionsMenu";
import { map } from "constants/screenElements/fundTable";
import FundTableMetricFormatter from "components/Global/Metric/FundTableMetricFormatter";

const getMetricValue = (fund, metricId, dotpath) => {
  const val = _.get(fund, dotpath, null);

  return FundTableMetricFormatter[metricId](val);
};

function formatMFDebtUnit(mfDebt, mfUnit) {
  if (mfUnit && mfUnit !== 0) {
    return numberFormatter.maskCurrency((mfDebt / mfUnit));
  }

  return 0;
}
const editRedirect = id => `/funds/${id}/edit`;

const FundTableColumns = () => [
  {
    id: "menu",
    isBaseColumn: true,
    dataField: null,
    layoutOrder: 99,
    numeric: false,
    Cell: ({ fund }) => (
      <TableCell className="loansList_Menu" key="menu">
        <FundTableOptionsMenu id={fund.id} editRedirect={editRedirect} />
      </TableCell>
    )
  },
  {
    id: "name",
    isBaseColumn: true,
    dataField: "name",
    layoutOrder: -1,
    numeric: false,
    label: "Name",
    Cell: ({ fund: { name, id } }) => (
      <TableCell className="loansList_Link" id={`fundNameCell${id}`}>
        <Link to={`/portfolio/funds/${id}`}>{name}</Link>
      </TableCell>
    )
  },
  {
    id: map.WEIGHTED_AVERAGE_INTEREST_RATE,
    isBaseColumn: false,
    dataField: "fundMetricsV2.weightedAverageInterestRate",
    numeric: false,
    label: "Weighted Average Interest Rate",
    Cell: data => {
      const id = _.get(data, "fund.id");
      const value = getMetricValue(data.fund, map.WEIGHTED_AVERAGE_INTEREST_RATE, "fundMetricsV2.weightedAverageInterestRate");

      return (
        <TableCell id={`fundWeightedAverageInterestRateCell${id}`}>{value}</TableCell>
      );
    }
  },
  {
    id: map.TOTAL_OUTSTANDING_DEBT,
    isBaseColumn: false,
    dataField: "fundMetricsV2.totalOutstandingDebt",
    numeric: false,
    label: "Total Outstanding Debt",
    Cell: data => {
      const id = _.get(data, "fund.id");
      const value = getMetricValue(data.fund, map.TOTAL_OUTSTANDING_DEBT, "fundMetricsV2.totalOutstandingDebt");

      return (
        <TableCell id={`fundTotalOutStandingDebtCell${id}`}>{value}</TableCell>
      );
    }
  },
  {
    id: map.CASH_EQUITY,
    isBaseColumn: false,
    dataField: "fundMetricsV2.cashEquity",
    numeric: false,
    label: "Cash Equity",
    Cell: data => {
      const id = _.get(data, "fund.id");
      const value = getMetricValue(data.fund, map.CASH_EQUITY, "fundMetricsV2.cashEquity");

      return (
        <TableCell id={`fundCashEquityCell${id}`}>{value}</TableCell>
      );
    }
  },
  {
    id: map.UNFUNDED_COMMITTED_DEBT,
    isBaseColumn: false,
    dataField: "fundMetricsV2.unfundedCommittment",
    numeric: false,
    label: "Unfunded Committed Debt",
    Cell: data => {
      const id = _.get(data, "fund.id");
      const value = getMetricValue(data.fund, map.UNFUNDED_COMMITTED_DEBT, "fundMetricsV2.unfundedCommittment");

      return (
        <TableCell id={`fundUnfundedCommittedDebtCell${id}`}>{value}</TableCell>
      );
    }
  },
  {
    id: map.LTV,
    isBaseColumn: false,
    dataField: "fundMetricsV2.ltv",
    numeric: false,
    label: "LTV",
    Cell: data => {
      const id = _.get(data, "fund.id");
      const value = getMetricValue(data.fund, map.LTV, "fundMetricsV2.ltv");

      return (
        <TableCell id={`fundLTVCell${id}`}>{value}</TableCell>
      );
    }
  },
  {
    id: map.DEBT_YIELD,
    isBaseColumn: false,
    dataField: "fundMetricsV2.debtYieldPercent",
    numeric: false,
    label: "Debt Yield",
    Cell: data => {
      const id = _.get(data, "fund.id");
      const value = getMetricValue(data.fund, map.DEBT_YIELD, "fundMetricsV2.debtYieldPercent");

      return (
        <TableCell id={`fundDebtYieldCell${id}`}>{value}</TableCell>
      );
    }
  },
  {
    id: map.DSCR,
    isBaseColumn: false,
    dataField: "fundMetricsV2.dscrLender",
    numeric: false,
    label: "DSCR",
    Cell: data => {
      const id = _.get(data, "fund.id");
      const value = getMetricValue(data.fund, map.DSCR, "fundMetricsV2.dscrLender");

      return (
        <TableCell id={`fundDSCRCell${id}`}>{value}</TableCell>
      );
    }
  },
  {
    id: map.DSCR_ACTUAL,
    isBaseColumn: false,
    dataField: "fundMetricsV2.dscrActual",
    numeric: false,
    label: "DSCR",
    Cell: data => {
      const id = _.get(data, "fund.id");
      const value = getMetricValue(data.fund, map.DSCR_ACTUAL, "fundMetricsV2.dscrActual");

      return (
        <TableCell id={`fundDSCRCell${id}`}>{value}</TableCell>
      );
    }
  },
  {
    id: map.DSCR_LENDER,
    isBaseColumn: false,
    dataField: "fundMetricsV2.dscrLender",
    numeric: false,
    label: "DSCR",
    Cell: data => {
      const id = _.get(data, "fund.id");
      const value = getMetricValue(data.fund, map.DSCR_LENDER, "fundMetricsV2.dscrLender");

      return (
        <TableCell id={`fundDSCRCell${id}`}>{value}</TableCell>
      );
    }
  },
  {
    id: map.FIXED_FLOATING_HEDGED_DEBT_PERCENT,
    isBaseColumn: false,
    dataField: "fundMetricsV2.fixedDebtPercent",
    numeric: false,
    label: "Fixed/Floating/Hedged Debt (%)",
    Cell: data => {
      const id = _.get(data, "fund.id");
      const fixedDebt = _.get(data, "fund.fundMetricsV2.fixedDebtPercent", null);
      const floatingDebt = _.get(data, "fund.fundMetricsV2.floatingDebtPercent", null);
      const hedgedDebt = _.get(data, "fund.fundMetricsV2.hedgedDebtPercent", null);
      const value = FundTableMetricFormatter[map.FIXED_FLOATING_HEDGED_DEBT_PERCENT](fixedDebt, floatingDebt, hedgedDebt);

      return (
        <TableCell id={`fundFixed/Floating/HedgedDebt(%)Cell${id}`}>{value}</TableCell>
      );
    }
  },
  {
    id: map.HEDGED_PV01,
    isBaseColumn: false,
    dataField: null,
    numeric: false,
    label: "Hedged PV01",
    Cell: data => {
      const id = _.get(data, "fund.id");
      const value = getMetricValue(data.fund, map.HEDGED_PV01);

      return (
        <TableCell id={`fundHedgedPV01Cell${id}`}>{value}</TableCell>
      );
    }
  },
  {
    id: map.UNHEDGED_PV01,
    isBaseColumn: false,
    dataField: null,
    numeric: false,
    label: "Unhedged PV01",
    Cell: data => {
      const id = _.get(data, "fund.id");
      const value = getMetricValue(data.fund, map.UNHEDGED_PV01);

      return (
        <TableCell id={`fundUnhedgedPV01Cell${id}`}>{value}</TableCell>
      );
    }
  },
  {
    id: map.NOI,
    isBaseColumn: false,
    dataField: "fundMetricsV2.noi",
    numeric: false,
    label: "NOI",
    Cell: data => {
      const id = _.get(data, "fund.id");
      const value = getMetricValue(data.fund, map.NOI, "fundMetricsV2.noi");

      return (
        <TableCell id={`fundTotalNOICell${id}`}>{value}</TableCell>
      );
    }
  },
  {
    id: map.NOI_ACTUAL,
    isBaseColumn: false,
    dataField: "fundMetricsV2.noiActual",
    numeric: false,
    label: "NOI Actual",
    Cell: data => {
      const id = _.get(data, "fund.id");
      const value = getMetricValue(data.fund, map.NOI_ACTUAL, "fundMetricsV2.noiActual");

      return (
        <TableCell id={`fundTotalNOICell${id}`}>{value}</TableCell>
      );
    }
  },
  {
    id: map.PROPERTY_VALUE,
    isBaseColumn: false,
    dataField: "fundMetricsV2.propertyValue",
    numeric: false,
    label: "Total Property Value",
    Cell: data => {
      const id = _.get(data, "fund.id");
      const value = getMetricValue(data.fund, map.PROPERTY_VALUE, "fundMetricsV2.propertyValue");

      return (
        <TableCell id={`fundTotalPropertyValueCell${id}`}>{value}</TableCell>
      );
    }
  },
  {
    id: map.PREPAYMENT,
    isBaseColumn: false,
    dataField: "fundMetricsV2.prepaymentPenalty",
    numeric: false,
    label: "Total Prepayment",
    Cell: data => {
      const id = _.get(data, "fund.id");
      const value = getMetricValue(data.fund, map.PREPAYMENT, "fundMetricsV2.prepaymentPenalty");

      return (
        <TableCell id={`fundTotalPrepaymentCell${id}`}>{value}</TableCell>
      );
    }
  },
  {
    id: map.RSF,
    isBaseColumn: false,
    dataField: "fundMetricsV2.rsf",
    numeric: false,
    label: "Total RSF",
    Cell: data => {
      const id = _.get(data, "fund.id");
      const value = getMetricValue(data.fund, map.RSF, "fundMetricsV2.rsf");

      return (
        <TableCell id={`fundTotalRSFCell${id}`}>{value}</TableCell>
      );
    }
  },
  {
    id: map.MF_UNITS,
    isBaseColumn: false,
    dataField: "fundMetricsV2.mfUnits",
    numeric: false,
    label: "Total MF Units",
    Cell: data => {
      const id = _.get(data, "fund.id");
      const value = getMetricValue(data.fund, map.MF_UNITS, "fundMetricsV2.mfUnits");

      return (
        <TableCell id={`fundTotalMFUnitsCell${id}`}>{value}</TableCell>
      );
    }
  },
  {
    id: map.DEBT_RSF,
    isBaseColumn: false,
    dataField: "fundMetricsV2.debtRsf",
    numeric: false,
    label: "Debt/RSF",
    Cell: data => {
      const id = _.get(data, "fund.id");
      const value = getMetricValue(data.fund, map.DEBT_RSF, "fundMetricsV2.debtRsf");

      return (
        <TableCell id={`fundDebt/RSVCell${id}`}>{value}</TableCell>
      );
    }
  },
  {
    id: map.DEBT_MF_UNIT,
    isBaseColumn: false,
    dataField: "fundMetricsV2.debtMfUnit",
    numeric: false,
    label: "Debt/MF Unit",
    Cell: data => {
      const id = _.get(data, "fund.id");
      const value = getMetricValue(data.fund, map.DEBT_MF_UNIT, "fundMetricsV2.debtMfUnit");

      return (
        <TableCell id={`fundDebt/MFUnitCell${id}`}>{value}</TableCell>
      );
    }
  },
  {
    id: map.AGGREGATE_EQUITY,
    isBaseColumn: false,
    dataField: "fundMetricsV2.aggregateEquity",
    numeric: false,
    label: "Aggregate Equity",
    Cell: data => {
      const id = _.get(data, "fund.id");
      const value = getMetricValue(data.fund, map.AGGREGATE_EQUITY, "fundMetricsV2.aggregateEquity");

      return (
        <TableCell id={`fundAggregateEquityCell${id}`}>{value}</TableCell>
      );
    }
  },
  {
    id: map.PROJECTED_RATE,
    isBaseColumn: false,
    dataField: "fundMetricsV2.projectedRate",
    numeric: false,
    label: "Projected Rate",
    Cell: data => {
      const id = _.get(data, "fund.id");
      const value = getMetricValue(data.fund, map.PROJECTED_RATE, "fundMetricsV2.projectedRate");

      return (
        <TableCell id={`fundProjectedRateCell${id}`}>{value}</TableCell>
      );
    }
  },
  {
    id: map.REFI_PROCEEDS,
    isBaseColumn: false,
    dataField: "fundMetricsV2.loanProceeds",
    numeric: false,
    label: "Total Outstanding Debt",
    Cell: data => {
      const id = _.get(data, "fund.id");
      const value = getMetricValue(data.fund, map.REFI_PROCEEDS, "fundMetricsV2.loanProceeds");

      return (
        <TableCell id={`fundloanProceedsCell${id}`}>{value}</TableCell>
      );
    }
  },
  {
    id: map.REFI_CASH_OUT,
    isBaseColumn: false,
    dataField: "fundMetricsV2.refiCashOut",
    numeric: false,
    label: "Refi Cash Out",
    Cell: data => {
      const id = _.get(data, "fund.id");
      const value = getMetricValue(data.fund, map.REFI_CASH_OUT, "fundMetricsV2.refiCashOut");

      return (
        <TableCell id={`fundRefiCashOutCell${id}`}>{value}</TableCell>
      );
    }
  },
  {
    id: map.ALL_IN_EFFECTIVE_RATE,
    isBaseColumn: false,
    dataField: "fundMetricsV2.allInEffectiveRate",
    numeric: false,
    label: "All In Effective Rate",
    Cell: data => {
      const id = _.get(data, "fund.id");
      const value = getMetricValue(data.fund, map.ALL_IN_EFFECTIVE_RATE, "fundMetricsV2.allInEffectiveRate");

      return (
        <TableCell id={`fundAllInEffectiveRateCell${id}`}>{value}</TableCell>
      );
    }
  }

];

export default FundTableColumns;
